import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import {
  Plus, Paperclip, Upload, Trash2, ChevronRight, ChevronLeft,
  Clock, CheckCircle2, FileText, Download, X, Calendar,
  Search, RefreshCw, AlertCircle, ListChecks, Check,
  Send, Archive, ChevronDown
} from "lucide-react";
import { chunkedUpload } from "@/utils/chunkedUpload";

const ESTADOS = [
  { key: "aguarda_revisao", label: "Aguarda Revisão", short: "Aguarda", color: "#b45309", bg: "#fef9c3", border: "#fde047", dot: "#f59e0b" },
  { key: "em_revisao",      label: "Em Revisão",      short: "Em Revisão", color: "#0b679e", bg: "#eff6ff", border: "#93c5fd", dot: "#0b679e" },
  { key: "concluido",       label: "Concluído",       short: "Concluído", color: "#065f46", bg: "#f0fdf4", border: "#86efac", dot: "#10b981" },
];

const MS_PER_DAY = 86400000;

function deadlineInfo(data_prazo) {
  if (!data_prazo) return null;
  const diff = Math.ceil((new Date(data_prazo) - new Date()) / MS_PER_DAY);
  if (diff < 0)  return { label: `${Math.abs(diff)}d em atraso`, cls: "text-red-700 bg-red-100" };
  if (diff <= 3) return { label: `${diff}d restantes`,           cls: "text-orange-700 bg-orange-100" };
  return { label: new Date(data_prazo).toLocaleDateString("pt-PT"), cls: "text-slate-500 bg-slate-100" };
}

function initials(name) {
  return (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
}

// ── Card ──────────────────────────────────────────────────────────────────────
function TrabalhoCard({ t, onEstado, onDelete, onOpen, onSubmeter }) {
  const idx = ESTADOS.findIndex(e => e.key === t.estado);
  const dl = deadlineInfo(t.data_prazo);
  return (
    <div
      data-testid={`trabalho-card-${t.id}`}
      onClick={() => onOpen(t)}
      className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-sm hover:shadow-md transition-all cursor-pointer group"
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <h3 className="text-sm font-semibold text-slate-900 leading-snug flex-1">{t.titulo}</h3>
        <button data-testid={`delete-trabalho-${t.id}`}
          onClick={e => { e.stopPropagation(); onDelete(t.id); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-300 hover:text-red-500 transition-all flex-shrink-0">
          <Trash2 size={13} />
        </button>
      </div>

      {t.processo_numero && (
        <span className="inline-block text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-[#0b679e] font-semibold mb-1.5">
          {t.processo_numero}
        </span>
      )}

      {t.descricao && (
        <p className="text-xs text-slate-400 line-clamp-2 mb-1.5">{t.descricao}</p>
      )}

      {dl && (
        <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium mb-2 ${dl.cls}`}>
          <Calendar size={9} /> {dl.label}
        </div>
      )}

      {t.checklist && t.checklist.length > 0 && (() => {
        const done = t.checklist.filter(i => i.feito).length;
        const total = t.checklist.length;
        const allDone = done === total;
        return (
          <div className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium mb-2 ${allDone ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
            <ListChecks size={9} /> {done}/{total} {allDone ? "concluídos" : "pendentes"}
          </div>
        );
      })()}

      {t.estado === "concluido" && (
        <div className="inline-flex items-center gap-1.5 text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 border border-amber-200 mb-2">
          <Clock size={9} /> Por Submeter
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-2.5">
        {t.ficheiro_original_url && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}${t.ficheiro_original_url}`}
            target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] text-[#0b679e] hover:text-[#F5A623] transition-colors">
            <Paperclip size={10} /> Original
          </a>
        )}
        {t.ficheiro_final_url && (
          <a href={`${process.env.REACT_APP_BACKEND_URL}${t.ficheiro_final_url}`}
            target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
            className="inline-flex items-center gap-1 text-[11px] text-emerald-600 hover:text-emerald-700 font-semibold transition-colors">
            <Download size={10} /> Final
          </a>
        )}
      </div>

      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <div className="flex items-center gap-1.5">
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-[9px] font-bold"
            style={{ backgroundColor: "#0b679e" }}>
            {initials(t.criado_por_nome)}
          </div>
          <span className="text-[11px] text-slate-400 truncate max-w-[90px]">
            {t.criado_por_nome?.split(" ")[0]}
          </span>
        </div>
        <div className="flex gap-0.5 items-center" onClick={e => e.stopPropagation()}>
          {idx > 0 && (
            <button onClick={() => onEstado(t.id, ESTADOS[idx - 1].key)}
              title={`Mover para ${ESTADOS[idx-1].label}`}
              className="p-1 rounded hover:bg-slate-100 text-slate-300 hover:text-slate-600 transition-colors">
              <ChevronLeft size={14} />
            </button>
          )}
          {t.estado === "concluido" ? (
            <button
              data-testid={`submeter-btn-${t.id}`}
              onClick={() => onSubmeter(t.id)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors ml-1">
              <Send size={10} /> Submetido
            </button>
          ) : (
            idx < ESTADOS.length - 1 && (
              <button onClick={() => onEstado(t.id, ESTADOS[idx + 1].key)}
                title={`Mover para ${ESTADOS[idx+1].label}`}
                className="p-1 rounded hover:bg-slate-100 text-[#0b679e] hover:text-[#084e78] transition-colors">
                <ChevronRight size={14} />
              </button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────────
function DetailModal({ t, onClose, onRefresh }) {
  const { user } = useAuth();
  const canManage = user?.role === "admin" || user?.role === "administracao";

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [origUrl, setOrigUrl] = useState(t.ficheiro_original_url);
  const [origNome, setOrigNome] = useState(t.ficheiro_original_nome);
  const [finalUrl, setFinalUrl] = useState(t.ficheiro_final_url);
  const [finalNome, setFinalNome] = useState(t.ficheiro_final_nome);

  // Checklist state — sincroniza sempre que t.checklist mudar (ex: após load() no pai)
  const [checklist, setChecklist] = useState(t.checklist || []);
  const [newItemText, setNewItemText] = useState("");
  const [checklistLoading, setChecklistLoading] = useState(false);

  useEffect(() => {
    setChecklist(t.checklist || []);
  }, [t.checklist, t.id]);

  const doneCount = checklist.filter(i => i.feito).length;

  const addItem = async () => {
    if (!newItemText.trim()) return;
    setChecklistLoading(true);
    try {
      const { data } = await axios.post(`${API}/trabalhos/${t.id}/checklist`,
        { texto: newItemText.trim() }, { headers: getAuthHeader() });
      setChecklist(data.checklist || []);
      setNewItemText("");
      onRefresh();
    } catch(err) {
      alert(err.response?.data?.detail || "Erro ao adicionar ponto");
    } finally { setChecklistLoading(false); }
  };

  const toggleItem = async (itemId, feito) => {
    try {
      const { data } = await axios.put(`${API}/trabalhos/${t.id}/checklist/${itemId}`,
        { feito }, { headers: getAuthHeader() });
      setChecklist(data.checklist || []);
      onRefresh();
    } catch(err) {
      alert(err.response?.data?.detail || "Erro ao atualizar item");
    }
  };

  const deleteItem = async (itemId) => {
    try {
      const { data } = await axios.delete(`${API}/trabalhos/${t.id}/checklist/${itemId}`,
        { headers: getAuthHeader() });
      setChecklist(data.checklist || []);
      onRefresh();
    } catch(err) {
      alert(err.response?.data?.detail || "Erro ao remover item");
    }
  };

  const upload = async (file, endpoint) => {
    setUploading(true);
    setUploadError("");
    setUploadProgress(0);
    try {
      const uploaded = await chunkedUpload(file, setUploadProgress);
      await axios.post(`${API}/trabalhos/${t.id}/${endpoint}`,
        { file_url: uploaded.file_url, file_name: uploaded.file_name },
        { headers: getAuthHeader() }
      );
      if (endpoint === "ficheiro-original") { setOrigUrl(uploaded.file_url); setOrigNome(uploaded.file_name); }
      else { setFinalUrl(uploaded.file_url); setFinalNome(uploaded.file_name); }
      onRefresh();
      onClose();
    } catch(err) {
      setUploadError(err.response?.data?.detail || err.message || "Erro ao carregar ficheiro. Tente novamente.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteFile = async (type) => {
    try {
      await axios.delete(`${API}/trabalhos/${t.id}/ficheiro-${type}`, { headers: getAuthHeader() });
      if (type === "original") { setOrigUrl(null); setOrigNome(null); }
      else { setFinalUrl(null); setFinalNome(null); }
      setConfirmDelete(null);
      onRefresh();
    } catch(err) {
      alert(err.response?.data?.detail || "Erro ao eliminar ficheiro");
    }
  };

  const dl = deadlineInfo(t.data_prazo);
  const estado = ESTADOS.find(e => e.key === t.estado);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100 sticky top-0 bg-white z-10">
          <div className="flex-1 pr-4">
            <h2 className="text-base font-bold text-slate-900">{t.titulo}</h2>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {t.processo_numero && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#0b679e] font-medium">{t.processo_numero}</span>
              )}
              <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: estado?.bg, color: estado?.color }}>
                {estado?.label}
              </span>
              {dl && <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${dl.cls}`}><Calendar size={10} className="inline mr-1" />{dl.label}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-4">
          {t.descricao && <p className="text-sm text-slate-600">{t.descricao}</p>}

          <div className="text-xs text-slate-400">
            Criado por <span className="font-medium text-slate-600">{t.criado_por_nome}</span>
            {" · "}{new Date(t.created_at).toLocaleDateString("pt-PT")}
          </div>

          {/* ── Checklist de Revisão ── */}
          {t.estado === "em_revisao" && (
            <div className="rounded-xl border border-orange-200 bg-orange-50/40 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ListChecks size={14} className="text-orange-600" />
                  <span className="text-xs font-semibold text-orange-700 uppercase tracking-wide">Pontos a Corrigir</span>
                </div>
                {checklist.length > 0 && (
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${doneCount === checklist.length ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                    {doneCount}/{checklist.length}
                  </span>
                )}
              </div>

              {checklist.length > 0 && doneCount === checklist.length && (
                <div className="flex items-center gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg mb-3">
                  <CheckCircle2 size={14} className="text-emerald-600 flex-shrink-0" />
                  <span className="text-xs font-semibold text-emerald-700">Todas as alterações efetuadas!</span>
                </div>
              )}

              {checklist.length === 0 ? (
                <p className="text-xs text-orange-400 italic mb-3">
                  {canManage ? "Adicione os pontos a corrigir abaixo." : "Sem pontos de correção adicionados."}
                </p>
              ) : (
                <ul className="space-y-1.5 mb-3">
                  {checklist.map(item => (
                    <li key={item.id}
                      className={`flex items-start gap-2.5 p-2.5 rounded-lg transition-all ${item.feito ? "bg-emerald-50/80 border border-emerald-100" : "bg-white border border-orange-100"}`}>
                      <button
                        data-testid={`checklist-toggle-${item.id}`}
                        onClick={() => toggleItem(item.id, !item.feito)}
                        className={`flex-shrink-0 mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-all ${
                          item.feito ? "bg-emerald-500 border-emerald-500" : "border-orange-300 hover:border-emerald-400 bg-white"
                        }`}>
                        {item.feito && <Check size={10} className="text-white" strokeWidth={3} />}
                      </button>
                      <span className={`text-sm flex-1 leading-snug ${item.feito ? "line-through text-slate-400" : "text-slate-700"}`}>
                        {item.texto}
                      </span>
                      {item.feito && item.feito_por && (
                        <span className="text-[10px] text-emerald-600 flex-shrink-0 mt-0.5">
                          ✓ {item.feito_por.split(" ")[0]}
                        </span>
                      )}
                      {canManage && (
                        <button
                          data-testid={`checklist-delete-${item.id}`}
                          onClick={() => deleteItem(item.id)}
                          className="flex-shrink-0 p-0.5 text-slate-300 hover:text-red-400 transition-colors mt-0.5">
                          <X size={11} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {canManage && (
                <div className="flex gap-2">
                  <input
                    data-testid="checklist-new-item-input"
                    value={newItemText}
                    onChange={e => setNewItemText(e.target.value)}
                    onKeyDown={e => e.key === "Enter" && addItem()}
                    placeholder="Adicionar ponto a corrigir... (Enter)"
                    className="flex-1 px-3 py-1.5 border border-orange-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-orange-300 bg-white placeholder-orange-300"
                  />
                  <button
                    data-testid="checklist-add-btn"
                    onClick={addItem}
                    disabled={!newItemText.trim() || checklistLoading}
                    className="px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-medium hover:bg-orange-600 transition-colors disabled:opacity-40">
                    <Plus size={12} />
                  </button>
                </div>
              )}
            </div>
          )}

          {uploadError && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle size={14} className="flex-shrink-0" /> {uploadError}
            </div>
          )}

          {uploading && (
            <div className="space-y-1.5 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-[#0b679e]">
                <div className="w-4 h-4 border-2 border-[#0b679e] border-t-transparent rounded-full animate-spin flex-shrink-0" />
                A carregar ficheiro... {uploadProgress > 0 && `${uploadProgress}%`}
              </div>
              {uploadProgress > 0 && (
                <div className="w-full bg-blue-200 rounded-full h-1.5">
                  <div className="bg-[#0b679e] h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          )}

          {/* Ficheiro Original */}
          <div className="rounded-xl border border-slate-200 p-3.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Documento Original</p>
            {origUrl ? (
              <div className="flex items-center justify-between gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${origUrl}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-[#0b679e] hover:text-[#F5A623] transition-colors min-w-0">
                  <FileText size={14} className="flex-shrink-0" />
                  <span className="truncate">{origNome || "Abrir documento"}</span>
                </a>
                {confirmDelete === "original" ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-red-600">Eliminar?</span>
                    <button onClick={() => deleteFile("original")}
                      className="text-xs px-2 py-0.5 bg-red-600 text-white rounded font-medium hover:bg-red-700">Sim</button>
                    <button onClick={() => setConfirmDelete(null)}
                      className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded hover:bg-slate-300">Não</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <div className="relative overflow-hidden rounded-lg">
                      <span className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 cursor-pointer px-2 py-1">
                        <RefreshCw size={11} /> Substituir
                      </span>
                      <input type="file" accept=".pdf,.doc,.docx,.xlsx,.jpg,.png"
                        disabled={uploading}
                        onChange={e => e.target.files[0] && upload(e.target.files[0], "ficheiro-original")}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                    </div>
                    <button data-testid="delete-ficheiro-original" onClick={() => setConfirmDelete("original")}
                      className="p-1.5 text-slate-300 hover:text-red-500 transition-colors rounded hover:bg-red-50" title="Eliminar ficheiro">
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-lg">
                <div className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-slate-200 rounded-lg py-3 text-sm text-slate-400 hover:border-[#0b679e] hover:text-[#0b679e] transition-colors pointer-events-none">
                  <Upload size={14} /> Carregar documento original
                </div>
                <input type="file" accept=".pdf,.doc,.docx,.xlsx,.jpg,.png"
                  disabled={uploading}
                  onChange={e => e.target.files[0] && upload(e.target.files[0], "ficheiro-original")}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
              </div>
            )}
          </div>

          {/* Versão Final */}
          <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50/50 p-3.5">
            <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mb-2">Versão Final (Dra. Ana)</p>
            {finalUrl ? (
              <div className="flex items-center justify-between gap-2">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${finalUrl}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 font-semibold transition-colors min-w-0">
                  <Download size={14} className="flex-shrink-0" />
                  <span className="truncate">{finalNome || "Descarregar versão final"}</span>
                </a>
                {confirmDelete === "final" ? (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-red-600">Eliminar?</span>
                    <button onClick={() => deleteFile("final")}
                      className="text-xs px-2 py-0.5 bg-red-600 text-white rounded font-medium hover:bg-red-700">Sim</button>
                    <button onClick={() => setConfirmDelete(null)}
                      className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded hover:bg-slate-300">Não</button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <div className="relative overflow-hidden rounded-lg">
                      <span className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 cursor-pointer px-2 py-1">
                        <RefreshCw size={11} /> Substituir
                      </span>
                      <input type="file" accept=".pdf,.doc,.docx,.xlsx,.jpg,.png"
                        disabled={uploading}
                        onChange={e => e.target.files[0] && upload(e.target.files[0], "ficheiro-final")}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
                    </div>
                    <button data-testid="delete-ficheiro-final" onClick={() => setConfirmDelete("final")}
                      className="p-1.5 text-slate-300 hover:text-red-500 transition-colors rounded hover:bg-red-50" title="Eliminar ficheiro">
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative overflow-hidden rounded-lg">
                <div className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-emerald-300 rounded-lg py-3 text-sm text-emerald-600 hover:border-emerald-500 hover:bg-emerald-50 transition-colors pointer-events-none">
                  <Upload size={14} /> Carregar versão final corrigida
                </div>
                <input type="file" accept=".pdf,.doc,.docx,.xlsx,.jpg,.png"
                  disabled={uploading}
                  onChange={e => e.target.files[0] && upload(e.target.files[0], "ficheiro-final")}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed" />
              </div>
            )}
            {!finalUrl && (
              <p className="text-[10px] text-emerald-600 mt-1.5">Ao carregar a versão final, o trabalho é marcado como Concluído automaticamente.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Create Modal ──────────────────────────────────────────────────────────────
function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ titulo: "", descricao: "", processo_numero: "", processo_id: "", data_prazo: "" });
  const [file, setFile] = useState(null);
  const [search, setSearch] = useState("");
  const [processos, setProcessos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (search.length < 2) { setProcessos([]); return; }
    const t = setTimeout(async () => {
      const { data } = await axios.get(`${API}/processos`, { params: { search, limit: 6 }, headers: getAuthHeader() });
      setProcessos(data.processos || []);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const selectProcesso = (p) => {
    setForm(f => ({ ...f, processo_id: p.id, processo_numero: p.numero_processo || p.id }));
    setSearch(p.numero_processo || "");
    setProcessos([]);
  };

  const submit = async () => {
    if (!form.titulo.trim()) { setErrors({ titulo: "Obrigatório" }); return; }
    setSaving(true);
    try {
      let fileData = {};
      if (file) {
        const uploaded = await chunkedUpload(file);
        fileData = { file_url: uploaded.file_url, file_name: uploaded.file_name };
      }
      await axios.post(`${API}/trabalhos`, {
        titulo: form.titulo,
        descricao: form.descricao,
        processo_id: form.processo_id || null,
        processo_numero: form.processo_numero,
        data_prazo: form.data_prazo || null,
        ...fileData
      }, { headers: getAuthHeader() });
      onCreated();
      onClose();
    } catch(err) {
      alert(err.response?.data?.detail || "Erro ao criar trabalho");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Novo Trabalho</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3.5">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Título <span className="text-red-500">*</span></label>
            <input value={form.titulo} onChange={e => setForm(f => ({...f, titulo: e.target.value}))}
              placeholder="Ex: Recurso Tribunal, Parecer Jurídico..."
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e] ${errors.titulo ? "border-red-400" : "border-slate-300"}`} />
            {errors.titulo && <p className="text-xs text-red-500 mt-0.5">{errors.titulo}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Descrição</label>
            <textarea value={form.descricao} onChange={e => setForm(f => ({...f, descricao: e.target.value}))}
              rows={2} placeholder="Detalhes adicionais..."
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e] resize-none" />
          </div>
          <div className="relative">
            <label className="block text-xs font-medium text-slate-600 mb-1">Processo (opcional)</label>
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => { setSearch(e.target.value); setForm(f => ({...f, processo_id:"",processo_numero:""})); }}
                placeholder="Pesquisar nº processo ou cliente..."
                className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
            </div>
            {processos.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {processos.map(p => (
                  <button key={p.id} onClick={() => selectProcesso(p)}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0">
                    <span className="font-medium text-[#0b679e]">{p.numero_processo}</span>
                    {" · "}<span className="text-slate-500">{p.nome_cliente}</span>
                  </button>
                ))}
              </div>
            )}
            {form.processo_numero && (
              <p className="text-xs text-emerald-600 mt-0.5">✓ Processo {form.processo_numero} seleccionado</p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Prazo / Deadline</label>
            <input type="date" value={form.data_prazo} onChange={e => setForm(f => ({...f, data_prazo: e.target.value}))}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Documento (opcional)</label>
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center cursor-pointer hover:border-[#0b679e] transition-colors"
              onClick={() => document.getElementById("new-trab-file").click()}>
              <Upload size={16} className="mx-auto text-slate-400 mb-1" />
              <p className="text-xs text-slate-400">{file ? file.name : "Clique para seleccionar ficheiro"}</p>
            </div>
            <input id="new-trab-file" type="file" hidden onChange={e => setFile(e.target.files[0])} />
          </div>
        </div>
        <div className="flex gap-2 p-5 pt-0">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">Cancelar</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 px-4 py-2.5 rounded-lg text-sm text-white font-medium transition-colors disabled:opacity-50"
            style={{ backgroundColor: "#0b679e" }} data-testid="criar-trabalho-btn">
            {saving ? "A criar..." : "Criar Trabalho"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function TrabalhosList() {
  const [trabalhos, setTrabalhos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);
  const [showArquivo, setShowArquivo] = useState(false);
  const { user } = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await axios.get(`${API}/trabalhos`, { params: { search }, headers: getAuthHeader() });
    setTrabalhos(data);
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Mantém o modal de detalhe sincronizado com os dados mais recentes após cada load()
  useEffect(() => {
    if (detail) {
      const updated = trabalhos.find(t => t.id === detail.id);
      if (updated) setDetail(updated);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trabalhos]);

  const handleEstado = async (id, estado) => {
    await axios.put(`${API}/trabalhos/${id}`, { estado }, { headers: getAuthHeader() });
    load();
  };

  const handleSubmeter = async (id) => {
    await axios.put(`${API}/trabalhos/${id}`, { estado: "submetido" }, { headers: getAuthHeader() });
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Eliminar este trabalho?")) return;
    await axios.delete(`${API}/trabalhos/${id}`, { headers: getAuthHeader() });
    load();
  };

  const byEstado = (key) => trabalhos.filter(t => t.estado === key);
  const submetidos = trabalhos.filter(t => t.estado === "submetido");
  const kanbanTrabalhos = trabalhos.filter(t => t.estado !== "submetido");

  const counts = ESTADOS.map(e => ({ ...e, count: byEstado(e.key).length }));

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Trabalhos</h1>
          <p className="text-sm text-slate-500 mt-0.5">Fluxo de revisão — {kanbanTrabalhos.length} trabalho{kanbanTrabalhos.length !== 1 ? "s" : ""} em curso</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="novo-trabalho-btn"
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-medium shadow-sm hover:shadow-md transition-all"
          style={{ backgroundColor: "#0b679e" }}>
          <Plus size={15} /> Novo Trabalho
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Pesquisar trabalhos..."
          className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0b679e] bg-white" />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {counts.map(e => (
          <div key={e.key} className="bg-white rounded-xl border border-slate-200 px-4 py-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold"
              style={{ backgroundColor: e.bg, color: e.color }}>
              {e.count}
            </div>
            <span className="text-sm text-slate-600 font-medium">{e.short}</span>
          </div>
        ))}
      </div>

      {/* Kanban */}
      {loading ? (
        <div className="text-center py-16 text-slate-400">A carregar...</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ESTADOS.map(estado => {
            const cards = byEstado(estado.key);
            return (
              <div key={estado.key} data-testid={`kanban-col-${estado.key}`}
                className="rounded-xl p-1" style={{ backgroundColor: estado.bg }}>
                {/* Column header */}
                <div className="flex items-center gap-2 px-3 py-2.5 mb-2">
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: estado.dot }} />
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: estado.color }}>
                    {estado.label}
                  </span>
                  <span className="ml-auto text-xs font-semibold px-1.5 py-0.5 rounded-full text-white"
                    style={{ backgroundColor: estado.dot }}>
                    {cards.length}
                  </span>
                </div>
                {/* Cards */}
                <div className="space-y-2 px-1 pb-1 min-h-24">
                  {cards.length === 0 ? (
                    <div className="text-center py-8 text-xs opacity-50" style={{ color: estado.color }}>
                      Sem trabalhos
                    </div>
                  ) : (
                    cards.map(t => (
                      <TrabalhoCard key={t.id} t={t}
                        onEstado={handleEstado}
                        onDelete={handleDelete}
                        onOpen={setDetail}
                        onSubmeter={handleSubmeter} />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Arquivo — Trabalhos Submetidos */}
      {submetidos.length > 0 && (
        <div className="border border-slate-200 rounded-xl overflow-hidden">
          <button
            data-testid="toggle-arquivo-btn"
            onClick={() => setShowArquivo(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors">
            <div className="flex items-center gap-2">
              <Archive size={14} className="text-slate-500" />
              <span className="text-sm font-semibold text-slate-600">Submetidos</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">{submetidos.length}</span>
            </div>
            <ChevronDown size={14} className={`text-slate-400 transition-transform ${showArquivo ? "rotate-180" : ""}`} />
          </button>
          {showArquivo && (
            <div className="divide-y divide-slate-100">
              {submetidos.map(t => (
                <div key={t.id} data-testid={`submetido-row-${t.id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-white hover:bg-slate-50 transition-colors">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{t.titulo}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {t.processo_numero && (
                        <span className="text-[10px] text-[#0b679e] font-semibold">{t.processo_numero}</span>
                      )}
                      <span className="text-[10px] text-slate-400">{t.criado_por_nome?.split(" ")[0]}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-semibold">
                      <CheckCircle2 size={9} /> Submetido
                    </span>
                    <button
                      data-testid={`reverter-btn-${t.id}`}
                      onClick={() => handleEstado(t.id, "concluido")}
                      className="text-[10px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors">
                      Reverter
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {detail && <DetailModal t={detail} onClose={() => setDetail(null)} onRefresh={load} />}
    </div>
  );
}
